import React, { useEffect, useState } from 'react';
import './Transcript.css';
import { DEMO_RECORDING_TYPE } from 'utils/constants';

const Transcript = ({ demo_analysis, meetingParticipants, host, guest, hasDiscussedBusinessStatus }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUsingManualTranscription, setIsUsingManualTranscription] =
    useState(false);

  /**
   * This function is used to get the name of the speaker
   *
   * @param {object} speech object consisting speaker and text
   * @returns {text} speaker text
   */
  const getSpeakerName = (speech) => {
    // If we're using manual transcription, use the user_name directly
    if (isUsingManualTranscription) {
      return speech?.user_name || speech?.speaker;
    }

    // Otherwise use the original logic
    if (meetingParticipants?.host === speech?.speaker) {
      return host?.first_name;
    } else if (meetingParticipants?.guest === speech?.speaker) {
      return guest?.full_name;
    } else {
      return speech?.speaker;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Find the records for host, guests and mixed
        const hostRecord = demo_analysis?.find(
          (record) => record.recording_type === DEMO_RECORDING_TYPE.HOST
        );
        const guestRecords = demo_analysis?.filter(
          (record) => record.recording_type === DEMO_RECORDING_TYPE.GUEST
        );
        const mixedRecord = demo_analysis?.find(
          (record) => record.recording_type === DEMO_RECORDING_TYPE.MIXED
        );

        // Determine which URL to use
        let s3Url;

        // Check if host record exists and all guest records have conversation URLs
        const allGuestConversationsExist =
          guestRecords?.length > 0 &&
          guestRecords.every((record) => record.conversation_url);

        if (
          hostRecord?.conversation_url &&
          allGuestConversationsExist &&
          mixedRecord?.manual_transcription_url
        ) {
          // If host and all guest conversations exist, and manual transcription is available
          s3Url = mixedRecord?.manual_transcription_url;
          setIsUsingManualTranscription(true);
        } else {
          // Otherwise use the mixed conversation URL
          s3Url = mixedRecord?.conversation_url;
          setIsUsingManualTranscription(false);
        }

        if (!s3Url) {
          throw new Error('No transcript URL available');
        }

        const response = await fetch(s3Url, {
          method: 'GET',
          mode: 'cors',
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const jsonData = await response.json();
        setData(jsonData);
      } catch (err) {
        setError(err?.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [demo_analysis]);

  if(hasDiscussedBusinessStatus === "NOT_INITIATED" || hasDiscussedBusinessStatus === "IN_PROGRESS") {
    return <p> processing</p>
  }
  if (!demo_analysis) {
    return <p>No transcript available</p>;
  }

  if (loading) {
    return <p>Loading..</p>;
  }

  if (error) {
    return <p>Error: {error}</p>;
  }

  /**
   * Determines whether the given speech entry belongs to the host.
   *
   * - Manual transcription: compares `user_name` with host full name
   * - Auto transcription: compares speaker ID with meetingParticipants host
   *
   * Used to apply host-specific styling in the transcript UI.
   *
   * @param {Object} speech - Transcript speech object
   * @returns {boolean} True if speech is from host, otherwise false
   */
  const isHostSpeaker = (speech) => {
    // Manual transcript → match user_name with host full name
    if (isUsingManualTranscription) {
      const fullHostName = `${host?.first_name} ${host?.last_name}`.toLowerCase();
      return (speech?.user_name || "").toLowerCase() === fullHostName;
    }

    // Auto transcript → match meetingParticipants host ID
    return meetingParticipants?.host === speech?.speaker;
  };


  return (
    <div>
      {data?.map((speech) => (
        <div className="transcript-container">
          <span className="transcript-speaker-header"
            style={{
              color: isHostSpeaker(speech) ? "#1890ff" : "#000000",
              fontSize: "15px",
              fontWeight: 600
            }}
          >
            {getSpeakerName(speech)} :{' '}
          </span>{' '}
          <span className="transcript-speaker-text">{speech?.text}</span>
        </div>
      ))}
    </div>
  );
};

export default Transcript;
